import { memo, useState, useCallback, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { MAX_FILE_SIZE } from './useFileUpload'
import type { CreateStyleFormData } from '../../../../api/styles'

export type { CreateStyleFormData }

interface StyleCreatorFormProps {
  existingStyleIds: string[]
  onSubmit: (data: CreateStyleFormData) => void
  onCancel: () => void
  initialValues?: CreateStyleFormData
}

const MAX_IMAGE_DIMENSION = 2048
const ACCEPTED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp']
const MAX_IMAGES = 5

async function resizeImage(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      const { width, height } = img

      if (width <= MAX_IMAGE_DIMENSION && height <= MAX_IMAGE_DIMENSION) {
        resolve(file)
        return
      }

      const scale = MAX_IMAGE_DIMENSION / Math.max(width, height)
      const newWidth = Math.round(width * scale)
      const newHeight = Math.round(height * scale)

      const canvas = document.createElement('canvas')
      canvas.width = newWidth
      canvas.height = newHeight
      const ctx = canvas.getContext('2d')!

      ctx.fillStyle = '#FFFFFF'
      ctx.fillRect(0, 0, newWidth, newHeight)
      ctx.drawImage(img, 0, 0, newWidth, newHeight)

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to resize image'))
            return
          }
          const resizedFile = new File([blob], file.name.replace(/\.\w+$/, '.jpg'), {
            type: 'image/jpeg',
          })
          resolve(resizedFile)
        },
        'image/jpeg',
        0.9
      )
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to load image'))
    }
    img.src = url
  })
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
}

const StyleCreatorForm = memo(({
  existingStyleIds,
  onSubmit,
  onCancel,
  initialValues,
}: StyleCreatorFormProps) => {
  const { t } = useTranslation('canvas')
  const [name, setName] = useState(initialValues?.name ?? '')
  const [description, setDescription] = useState(initialValues?.description ?? '')
  const [referenceImages, setReferenceImages] = useState<File[]>(initialValues?.referenceImages ?? [])
  const [imagePreviews, setImagePreviews] = useState<string[]>([])
  const [touched, setTouched] = useState<{ name?: boolean; description?: boolean }>({})

  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (initialValues?.referenceImages?.length) {
      const urls = initialValues.referenceImages.map((f) => URL.createObjectURL(f))
      setImagePreviews(urls)
      return () => urls.forEach((u) => URL.revokeObjectURL(u))
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const previewsRef = useRef(imagePreviews)
  previewsRef.current = imagePreviews
  useEffect(() => {
    return () => {
      previewsRef.current.forEach((url) => URL.revokeObjectURL(url))
    }
  }, [])

  const nameSlug = slugify(name)
  const isNameDuplicate = name.trim().length > 0 && existingStyleIds.includes(nameSlug)
  const nameError = touched.name
    ? !name.trim()
      ? t('styleCreatorForm.nameRequired')
      : name.length > 50
        ? t('styleCreatorForm.nameTooLong')
        : isNameDuplicate
          ? t('styleCreatorForm.nameDuplicate')
          : null
    : null
  const descriptionError = touched.description
    ? !description.trim()
      ? t('styleCreatorForm.descriptionRequired')
      : description.length < 10
        ? t('styleCreatorForm.descriptionTooShort', { count: description.length })
        : description.length > 500
          ? t('styleCreatorForm.descriptionTooLong')
          : null
    : null

  const isValid =
    name.trim().length > 0 &&
    name.length <= 50 &&
    !isNameDuplicate &&
    description.trim().length >= 10 &&
    description.length <= 500

  const isDirty =
    name.trim().length > 0 ||
    description.trim().length > 0 ||
    referenceImages.length > 0

  const handleCancel = useCallback(() => {
    if (isDirty) {
      if (!window.confirm(t('styleCreatorForm.unsavedChangesConfirm'))) {
        return
      }
    }
    onCancel()
  }, [isDirty, onCancel, t])

  const handleAddImages = useCallback(
    async (files: FileList | File[]) => {
      const fileArray = Array.from(files)
      const remaining = MAX_IMAGES - referenceImages.length
      const toProcess = fileArray.slice(0, remaining)

      for (const file of toProcess) {
        if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
          continue
        }
        if (file.size > (MAX_FILE_SIZE.image ?? 10 * 1024 * 1024)) {
          continue
        }

        try {
          const resized = await resizeImage(file)
          const previewUrl = URL.createObjectURL(resized)

          setReferenceImages((prev) => [...prev, resized])
          setImagePreviews((prev) => [...prev, previewUrl])
        } catch {
          // Skip files that fail to load/resize
        }
      }
    },
    [referenceImages.length]
  )

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        handleAddImages(e.target.files)
      }
      e.target.value = ''
    },
    [handleAddImages]
  )

  const handleRemoveImage = useCallback((index: number) => {
    setImagePreviews((prev) => {
      URL.revokeObjectURL(prev[index])
      return prev.filter((_, i) => i !== index)
    })
    setReferenceImages((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const handleSubmit = useCallback(() => {
    setTouched({ name: true, description: true })
    if (!isValid) return
    onSubmit({ name: name.trim(), description: description.trim(), referenceImages })
  }, [isValid, name, description, referenceImages, onSubmit])

  return (
    <div className="px-6 py-4 overflow-y-auto flex-1 min-h-0">
      <div className="max-w-lg mx-auto space-y-6">
        {/* Style Name */}
        <div>
          <label className="block text-sm font-medium text-foreground/70 mb-1.5">
            {t('styleCreatorForm.styleName')}
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, name: true }))}
            maxLength={50}
            placeholder={t('styleCreatorForm.namePlaceholder')}
            className="w-full px-3 py-2 rounded-lg bg-surface-1 border border-white/10
              text-foreground placeholder:text-foreground/30
              focus:outline-none focus:ring-2 focus:ring-pink-500/40 focus:border-pink-500/40
              transition-colors"
          />
          <div className="flex justify-between mt-1">
            {nameError ? (
              <span className="text-xs font-medium text-red-400">{nameError}</span>
            ) : (
              <span />
            )}
            <span className="text-xs font-medium text-foreground/30">{name.length}/50</span>
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-foreground/70 mb-1.5">
            {t('styleCreatorForm.describeYourStyle')}
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, description: true }))}
            maxLength={500}
            rows={5}
            placeholder={t('styleCreatorForm.descriptionPlaceholder')}
            className="w-full px-3 py-2 rounded-lg bg-surface-1 border border-white/10
              text-foreground placeholder:text-foreground/30 resize-none
              focus:outline-none focus:ring-2 focus:ring-pink-500/40 focus:border-pink-500/40
              transition-colors"
          />
          <div className="flex justify-between mt-1">
            {descriptionError ? (
              <span className="text-xs font-medium text-red-400">{descriptionError}</span>
            ) : (
              <span />
            )}
            <span className="text-xs font-medium text-foreground/30">{description.length}/500</span>
          </div>
        </div>

        {/* Reference Images */}
        <div>
          <label className="block text-sm font-medium text-foreground/70 mb-1.5">
            {t('styleCreatorForm.referenceImages')}{' '}
            <span className="text-foreground/30 font-normal">{t('styleCreatorForm.optionalUpTo5')}</span>
          </label>
          <div className="flex flex-wrap gap-3">
            {imagePreviews.map((url, i) => (
              <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-white/10">
                <img
                  src={url}
                  alt={t('styleCreatorForm.altReference', { index: i + 1 })}
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={() => handleRemoveImage(i)}
                  className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/70
                    flex items-center justify-center text-white/80 hover:text-white
                    hover:bg-red-600 transition-colors text-xs font-medium"
                >
                  ×
                </button>
              </div>
            ))}

            {referenceImages.length < MAX_IMAGES && (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-20 h-20 rounded-lg border-2 border-dashed border-white/20
                  flex flex-col items-center justify-center gap-1
                  text-foreground/30 hover:text-foreground/50 hover:border-white/30
                  transition-colors cursor-pointer"
              >
                <span className="text-lg font-medium leading-none">+</span>
                <span className="text-xxs">{t('styleCreatorForm.add')}</span>
              </button>
            )}
          </div>

          <p className="text-xs font-medium text-foreground/30 mt-2">
            {t('styleCreatorForm.uploadHint')}
          </p>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            className="hidden"
            onChange={handleFileInputChange}
          />
        </div>

        {/* Action buttons */}
        <div className="flex justify-end gap-3 pt-2">
          <button
            onClick={handleCancel}
            className="px-4 py-2 rounded-lg text-sm text-foreground/70
              hover:text-foreground hover:bg-surface-2 transition-colors"
          >
            {t('styleCreatorForm.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isValid}
            className="px-4 py-2 rounded-lg text-sm font-medium
              bg-pink-600 text-white hover:bg-pink-500
              disabled:opacity-50 disabled:cursor-not-allowed
              transition-colors"
          >
            {t('styleCreatorForm.createStyle')}
          </button>
        </div>
      </div>
    </div>
  )
})

StyleCreatorForm.displayName = 'StyleCreatorForm'
export { StyleCreatorForm }
